import { useEffect } from 'react';
import { Navigate, useLocation } from 'react-router';
import { shouldNavigateToFinance } from 'utils/navigation';
import { isLogged } from 'utils/session';
import { useApplicationQuery, useClientQuery } from 'hooks/webapi';
import { ClientApplication } from 'hooks/webapi/client/useApplicationQuery';
import { Client } from 'hooks/webapi/client/useClientQuery';
import * as R from './routes';

type Props = {
  children: React.JSX.Element;
};

const ROUTES_APPLICATION_NOT_NEEDED = [
  R.LOGIN,
  R.INTRO,
  R.INTRO_START_APP,
  R.INTRO_2,
  R.WELCOME_BACK_NEW,
  R.WAITING,
  R.PSD2_VERIFIED,
  R.UPSALE_CHANGE_AMOUNT,
];

const checkBEConditionsByRoute = (
  client: Client | undefined,
  application: ClientApplication | undefined,
) => {
  if (!client || !application) return true;

  const pathName = window.location.pathname;

  switch (pathName) {
    case R.FINANCE:
      return shouldNavigateToFinance(client);
    case R.PHONE_VERIFICATION:
      return !client?.mobilePhoneVerified;
    /*
    case R.MANUAL_PSD2_CONSENTS:
      return (
        application?.resolution === 'MANUAL' && application?.status === 'OPEN'
      );
    */
    case R.MANUAL_REVIEW:
      return (
        application?.resolution === 'MANUAL' &&
        application?.status === 'OPEN' &&
        application?.resolutionDetail !== 'INSUFFICIENT_PSD2_INCOME' &&
        application?.resolutionMessage !== 'ASK_FOR_DOCUMENTS'
      );
    case R.MANUAL_UPLOAD_ID:
      return !client?.hasAttachments;
    case R.REJECTED_OFFER:
      return application?.resolution === 'REJECTED';
    case R.UPSALE:
      return (
        application?.resolution === 'REJECTED' &&
        application?.resolutionDetail === 'UPSALE_OFFER'
      );
    case R.DOWNSALE:
      return (
        application?.resolution === 'REJECTED' &&
        application?.resolutionDetail === 'NEW_OFFER'
      );
    case R.APPROVED:
      return application?.resolution === 'APPROVED';
    default:
      if (
        !ROUTES_APPLICATION_NOT_NEEDED.includes(pathName) &&
        application.status !== 'OPEN'
      ) {
        return false;
      }

      return true;
  }
};

const SESSION_ROUTE_AVAILABLE_KEY = 'last_route_available';

export default function GuardRoute({ children }: Props) {
  const { pathname } = useLocation();
  const { data: client, refetch: refetchClient } = useClientQuery();
  const { data: application, refetch: refetchApplication } =
    useApplicationQuery();

  // Fetch data for guard routes if it's possible
  useEffect(() => {
    if (!isLogged()) return;

    if (!client) refetchClient();
    if (!application) refetchApplication();
  }, []);

  const isDataReadyToBeChecked = Boolean(client) && Boolean(application);

  if (!isDataReadyToBeChecked) return children;

  const isClientAllowedToVisitRoute = checkBEConditionsByRoute(
    client,
    application,
  );
  const lastRouteAvailable =
    sessionStorage.getItem(SESSION_ROUTE_AVAILABLE_KEY) || pathname;

  if (!isClientAllowedToVisitRoute && pathname !== lastRouteAvailable) {
    return <Navigate to={lastRouteAvailable || '/'} replace />;
  }
  sessionStorage.setItem(SESSION_ROUTE_AVAILABLE_KEY, pathname);

  return children;
}
